/**
 * space/shelves.js — the prize shelves (GDD 9.2).
 *
 * Every plush in `save.prizes.plush` gets a slot on a shelf unit. Units hold
 * six slots each; two exist from the start (the GDD's twelve) and three more
 * unlock with Stars. Rearranging is lift-then-place: point at a plush and
 * select to raise it, point at any slot and select to set it down. The order is
 * persisted so a shelf you curated stays curated.
 *
 * Rarity drives the show-off effects the GDD asks for. The GDD names the four
 * tiers but not which prize is which, so the mapping lives here: prize size
 * crossed with the difficulty of the stall that paid it out.
 *
 * @module space/shelves
 */

import * as THREE from 'three';
import { SPACE, SHELF_EXPANSION_COSTS } from '../constants.js';
import * as factory from '../assets3d/factory.js';
import { pointer, createLabel } from './pointer.js';
import { shelfTheme, plushRarity, glowSprite, stallTint, luminance } from './rarity.js';

const SLOTS_PER_UNIT = SPACE.shelfSlotsPerUnit;
const COLS = 3;
const BOARD_Y = [0.78, 1.46];

/**
 * The shelf wall of one room.
 */
export class ShelfWall {
  /**
   * @param {{ room: import('./space-world.js').SpaceRoom, readOnly?: boolean,
   *           onBuyExpansion?: () => void, onOpenThemes?: () => void }} opts
   */
  constructor(opts) {
    this.room = opts.room;
    this.readOnly = !!opts.readOnly;
    this.onBuyExpansion = opts.onBuyExpansion;
    this.onOpenThemes = opts.onOpenThemes;

    this.group = new THREE.Group();
    this.group.name = 'space-shelves';
    this.room.root.add(this.group);

    /** @type {Array<{index: number, position: THREE.Vector3, pad: THREE.Mesh}>} */
    this.slots = [];
    /** @type {Map<string, {prize: object, slot: number, object3D: THREE.Object3D, fx: object}>} */
    this.placed = new Map();
    /** @type {string|null} */ this.heldId = null;

    this._skin = null;
    this._elapsed = 0;
    this._idPrefix = `${this.room.name}-shelf`;
  }

  /**
   * Rebuild units, slots and displayed prizes from the model.
   * @param {{ plush: object[], arrangement: Record<string, number>,
   *           shelfSlots: number, theme: string }} model
   */
  rebuild(model) {
    this.dispose();
    this.model = model;

    const t = shelfTheme(model.theme);
    this._skin = {
      wood: new THREE.MeshStandardMaterial({ color: t.wood, roughness: 0.85 }),
      trim: new THREE.MeshStandardMaterial({
        color: t.trim, roughness: 0.42, metalness: 0.35,
        emissive: t.emissive, emissiveIntensity: t.emissive ? 0.9 : 0,
      }),
    };

    const units = Math.min(
      this.room.anchors.shelfBays.length,
      Math.max(1, Math.ceil(model.shelfSlots / SLOTS_PER_UNIT)),
    );
    for (let u = 0; u < units; u++) this._buildUnit(u);
    this._buildPlaques(units, model);
    this._placeAll(model);
  }

  /** @private */
  _buildUnit(unitIndex) {
    const bay = this.room.anchors.shelfBays[unitIndex];
    const unit = new THREE.Group();
    unit.position.copy(bay.position);
    unit.rotation.y = bay.rotation.y;
    unit.name = `shelf-unit-${unitIndex}`;
    this.group.add(unit);

    const back = new THREE.Mesh(new THREE.BoxGeometry(2.1, 2.0, 0.05), this._skin.wood);
    back.position.set(0, 1.0, 0.03);
    back.receiveShadow = true;
    unit.add(back);

    for (const sx of [-1, 1]) {
      const side = new THREE.Mesh(new THREE.BoxGeometry(0.07, 2.0, 0.44), this._skin.wood);
      side.position.set(sx * 1.05, 1.0, 0.23);
      side.castShadow = true;
      unit.add(side);
    }

    const top = new THREE.Mesh(new THREE.BoxGeometry(2.17, 0.08, 0.48), this._skin.trim);
    top.position.set(0, 2.02, 0.23);
    unit.add(top);

    BOARD_Y.forEach((by, b) => {
      const board = new THREE.Mesh(new THREE.BoxGeometry(2.1, 0.06, 0.44), this._skin.wood);
      board.position.set(0, by, 0.23);
      board.castShadow = true;
      board.receiveShadow = true;
      unit.add(board);

      // A lip of theme colour along each board front — this is what actually
      // reads as "neon arcade" or "golden trophy case" from across the room.
      const lip = new THREE.Mesh(new THREE.BoxGeometry(2.1, 0.035, 0.03), this._skin.trim);
      lip.position.set(0, by + 0.045, 0.445);
      unit.add(lip);

      for (let c = 0; c < COLS; c++) {
        const index = unitIndex * SLOTS_PER_UNIT + b * COLS + c;
        if (index >= this.model.shelfSlots) continue;
        const local = new THREE.Vector3((c - 1) * 0.68, by + 0.033, 0.2);
        this._addSlot(index, unit, local);
      }
    });
  }

  /** @private */
  _addSlot(index, unit, local) {
    const pad = new THREE.Mesh(
      new THREE.CircleGeometry(0.15, 18),
      new THREE.MeshBasicMaterial({
        color: 0x6fe7c4, transparent: true, opacity: 0.0, side: THREE.DoubleSide,
      }),
    );
    pad.rotation.x = -Math.PI / 2;
    pad.position.copy(local).add(new THREE.Vector3(0, 0.004, 0));
    unit.add(pad);

    const world = new THREE.Vector3();
    unit.updateWorldMatrix(true, false);
    pad.updateWorldMatrix(true, false);
    pad.getWorldPosition(world);

    const slot = { index, position: world.clone(), pad, local: local.clone(), unit };
    this.slots.push(slot);

    if (this.readOnly) return;
    pointer.register(pad, {
      id: `${this._idPrefix}:slot:${index}`,
      group: 'shelf-slot',
      label: `Slot ${index + 1}`,
      onHover: (on) => { if (this.heldId) pad.material.opacity = on ? 0.85 : 0.4; },
      onSelect: () => this.placeHeld(index),
    });
  }

  /** @private */
  _buildPlaques(units, model) {
    if (this.readOnly) return;
    const bay = this.room.anchors.shelfBays[Math.max(0, units - 1)];

    const themePlate = factory.createSign('', {
      w: 0.72, h: 0.2, lines: ['SHELF STYLE'], size: 34,
    });
    // The bay already faces into the room; adding PI turned the plaque around to
    // face the wall. createSign backs every sign with a solid plum board, so the
    // player saw that blank slab instead of the label — and a FrontSide material
    // is not hit by a raycast from behind, which made the plaque unclickable too.
    themePlate.position.set(bay.position.x, 2.22, bay.position.z - 0.5);
    themePlate.rotation.y = bay.rotation.y;
    this.group.add(themePlate);
    pointer.register(themePlate, {
      id: `${this._idPrefix}:themes`,
      group: 'shelf-ui',
      label: 'Shelf style',
      onSelect: () => this.onOpenThemes?.(),
    });

    const nextCost = SHELF_EXPANSION_COSTS[units - 2];
    const maxed = units >= this.room.anchors.shelfBays.length || nextCost === undefined;
    const expand = factory.createSign('', {
      w: 0.86, h: 0.24,
      lines: [maxed ? 'ALL SHELVES BUILT' : `+6 SLOTS  ${nextCost}*`],
      size: 32,
      bg: maxed ? '#232838' : '#1d3a2e',
    });
    expand.position.set(bay.position.x, 2.22, bay.position.z + 0.5);
    expand.rotation.y = bay.rotation.y;
    this.group.add(expand);
    if (!maxed) {
      pointer.register(expand, {
        id: `${this._idPrefix}:expand`,
        group: 'shelf-ui',
        label: 'Buy another shelf',
        onSelect: () => this.onBuyExpansion?.(),
      });
    }
  }

  // ── Prize display ─────────────────────────────────────────────────────────

  /** @private */
  _placeAll(model) {
    const taken = new Set();
    const assign = { ...(model.arrangement || {}) };

    // Honour a stored slot when it is in range and free, otherwise take the
    // lowest free one — a shelf downgrade must never lose a prize.
    for (const prize of model.plush) {
      const want = assign[prize.id];
      if (Number.isInteger(want) && want < model.shelfSlots && !taken.has(want)) {
        taken.add(want);
      } else {
        delete assign[prize.id];
      }
    }
    for (const prize of model.plush) {
      if (assign[prize.id] !== undefined) continue;
      let free = 0;
      while (taken.has(free)) free++;
      if (free >= model.shelfSlots) break;      // overflow: not displayed
      assign[prize.id] = free;
      taken.add(free);
    }

    this.assignment = assign;
    for (const prize of model.plush) {
      const slotIndex = assign[prize.id];
      if (slotIndex === undefined) continue;
      this._display(prize, slotIndex);
    }
  }

  /** @private */
  _display(prize, slotIndex) {
    const slot = this.slots.find((s) => s.index === slotIndex);
    if (!slot) return;

    const obj = factory.createPlush(prize.kind);
    const rarity = plushRarity(prize);
    const tint = new THREE.Color().setHSL(stallTint(prize.stallId), 0.62, 0.62);

    obj.traverse((c) => {
      if (!c.isMesh) return;
      // Factory materials are memoised and shared, so tint a copy or every
      // plush in the room changes colour with this one.
      c.material = c.material.clone();
      // Eyes, noses and pupils stay dark — tinting anything near-black turns a
      // face into a smudge. Tested by luminance rather than an exact colour so
      // it survives the asset agent restyling the prizes.
      if (c.material.color && luminance(c.material.color) > 0.12) {
        c.material.color.lerp(tint, 0.5);
      }
      c.castShadow = true;
    });

    obj.position.copy(slot.local);
    obj.rotation.y = Math.PI;
    slot.unit.add(obj);

    const fx = this._attachRarity(obj, rarity);
    this.placed.set(prize.id, { prize, slot: slotIndex, object3D: obj, fx, rarity });

    if (this.readOnly) return;
    pointer.register(obj, {
      id: `${this._idPrefix}:plush:${prize.id}`,
      group: 'shelf-plush',
      label: prize.kind,
      onHover: (on) => { obj.scale.setScalar(on ? 1.09 : 1); },
      onSelect: () => this.lift(prize.id),
    });
  }

  /** @private */
  _attachRarity(obj, rarity) {
    const fx = { rarity, mats: [], halo: null, sparks: [] };
    obj.traverse((c) => { if (c.isMesh && c.material.emissive) fx.mats.push(c.material); });

    if (rarity === 'common') return fx;

    const tone = rarity === 'legendary' ? 0xffd166 : rarity === 'rare' ? 0x9be7ff : 0xffe6a8;
    for (const m of fx.mats) {
      m.emissive.setHex(tone);
      m.emissiveIntensity = rarity === 'uncommon' ? 0.12 : 0.4;
    }

    if (rarity === 'rare' || rarity === 'legendary') {
      fx.halo = glowSprite(rarity === 'legendary' ? 0.85 : 0.6, tone);
      fx.halo.position.y = 0.2;
      obj.add(fx.halo);
    }
    if (rarity === 'legendary') {
      for (let i = 0; i < 4; i++) {
        const s = glowSprite(0.1, 0xfff2c0);
        s.userData.phase = (i / 4) * Math.PI * 2;
        obj.add(s);
        fx.sparks.push(s);
      }
    }
    return fx;
  }

  // ── Rearranging ───────────────────────────────────────────────────────────

  /**
   * Pick a plush up. Selecting a slot afterwards puts it down there.
   * @param {string} prizeId
   * @returns {boolean}
   */
  lift(prizeId) {
    if (this.readOnly) return false;
    const rec = this.placed.get(prizeId);
    if (!rec) return false;
    if (this.heldId && this.heldId !== prizeId) this._drop(this.heldId);
    this.heldId = prizeId;
    for (const s of this.slots) s.pad.material.opacity = 0.4;
    if (!rec.label) {
      rec.label = createLabel('Pick a slot', { scale: 0.3 });
      rec.label.position.set(0, 0.72, 0);
      rec.object3D.add(rec.label);
    }
    return true;
  }

  /**
   * Put the held plush into a slot, swapping with whatever is there.
   * @param {number} slotIndex
   * @returns {boolean}
   */
  placeHeld(slotIndex) {
    if (!this.heldId) return false;
    return this.place(this.heldId, slotIndex);
  }

  /**
   * Move a prize to a slot. Occupied slots swap rather than reject, which is
   * what "free placement" has to mean when every slot may be full.
   * @param {string} prizeId
   * @param {number} slotIndex
   * @returns {boolean}
   */
  place(prizeId, slotIndex) {
    const rec = this.placed.get(prizeId);
    const slot = this.slots.find((s) => s.index === slotIndex);
    if (!rec || !slot) return false;

    const occupant = [...this.placed.values()].find((p) => p.slot === slotIndex && p !== rec);
    const fromIndex = rec.slot;
    const fromSlot = this.slots.find((s) => s.index === fromIndex);

    rec.slot = slotIndex;
    slot.unit.add(rec.object3D);
    rec.object3D.position.copy(slot.local);
    this.assignment[prizeId] = slotIndex;

    if (occupant && fromSlot) {
      occupant.slot = fromIndex;
      fromSlot.unit.add(occupant.object3D);
      occupant.object3D.position.copy(fromSlot.local);
      this.assignment[occupant.prize.id] = fromIndex;
    }

    this._drop(prizeId);
    return true;
  }

  /** @private */
  _drop(prizeId) {
    const rec = this.placed.get(prizeId);
    if (rec?.label) {
      rec.object3D.remove(rec.label);
      rec.label.userData.dispose?.();
      rec.label = null;
    }
    if (this.heldId === prizeId) this.heldId = null;
    for (const s of this.slots) s.pad.material.opacity = 0;
  }

  // ── Queries ───────────────────────────────────────────────────────────────

  /** @returns {Array<{id, kind, stallId, slot, rarity, day}>} */
  list() {
    return [...this.placed.values()].map((p) => ({
      id: p.prize.id, kind: p.prize.kind, stallId: p.prize.stallId,
      day: p.prize.day, slot: p.slot, rarity: p.rarity,
    }));
  }

  /** @returns {number} */
  capacity() {
    return this.model ? this.model.shelfSlots : 0;
  }

  /** @returns {Record<string, number>} prizeId → slot */
  arrangement() {
    return { ...this.assignment };
  }

  // ── Frame ─────────────────────────────────────────────────────────────────

  update(dt) {
    this._elapsed += dt;
    const e = this._elapsed;

    for (const rec of this.placed.values()) {
      const { fx } = rec;
      if (fx.rarity === 'uncommon') {
        const k = 0.1 + 0.16 * (0.5 + 0.5 * Math.sin(e * 2.2 + rec.slot));
        for (const m of fx.mats) m.emissiveIntensity = k;
      } else if (fx.rarity === 'legendary') {
        for (const m of fx.mats) m.emissiveIntensity = 0.34 + 0.16 * Math.sin(e * 3 + rec.slot);
      }
      if (fx.halo) fx.halo.material.opacity = 0.55 + 0.2 * Math.sin(e * 1.7 + rec.slot);
      for (const s of fx.sparks) {
        const a = e * 1.5 + s.userData.phase;
        s.position.set(Math.cos(a) * 0.19, 0.2 + Math.sin(a * 1.7) * 0.12, Math.sin(a) * 0.19);
      }
      if (rec.prize.id === this.heldId) {
        const slot = this.slots.find((s) => s.index === rec.slot);
        if (slot) {
          rec.object3D.position.y = slot.local.y + 0.34 + Math.sin(e * 3.4) * 0.035;
          rec.object3D.rotation.y = Math.PI + e * 1.1;
        }
      }
    }
  }

  dispose() {
    for (const rec of this.placed.values()) {
      pointer.unregister(`${this._idPrefix}:plush:${rec.prize.id}`);
      rec.label?.userData.dispose?.();
    }
    for (const s of this.slots) pointer.unregister(`${this._idPrefix}:slot:${s.index}`);
    pointer.unregister(`${this._idPrefix}:expand`);
    pointer.unregister(`${this._idPrefix}:themes`);
    this.placed.clear();
    this.slots.length = 0;
    this.heldId = null;
    while (this.group.children.length) this.group.remove(this.group.children[0]);
  }
}
